import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentTools, type ToolCall, type ToolCallSummary } from '@/features/types/tools';
import { ToolCallContainer } from '../../ToolCallContainer';
import { ToolCallDataList } from '../../ToolCallDataList';
import { useToolCallParser, ToolCallState } from '../../useToolCallParser';
import { ToolCallError } from '../../ToolCallError';
import { MeetingCard } from './common/MeetingCard';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { ChatMarkDownFormatter } from '../../../ChatMarkDownFormatter';
import { formatSummaryContent } from './common/format-utils';

interface GetEngagementDetailsTool {
  name: AgentTools.chorus_get_engagement_details;
  args: {
    engagementId: string;
  };
}

export type GetEngagementDetailsToolCall = ToolCall<
  GetEngagementDetailsTool['name'],
  GetEngagementDetailsTool['args']
>;

export type GetEngagementDetailsToolCallSummary = ToolCallSummary<
  GetEngagementDetailsTool['name'],
  GetEngagementDetailsTool['args']
>;

interface GetEngagementDetailsResult {
  account: {
    id: number;
    name: string;
    type: string;
  };
  action_items?: string[];
  date: string;
  generated_subject: string;
  id: string;
  language: string;
  metrics: {
    name: string;
    value: number;
  }[];
  owner: {
    email: string;
    name: string;
    person_id: number;
    user_id: number;
  };
  participants?: {
    company_name: string;
    email: string;
    is_my_team: boolean;
    name: string;
    person_id: number;
    picture: string;
    title: string;
    type: string;
    user_id: number;
    zi_person_id: number;
  }[];
  status: string;
  subject: string;
  summary: string;
}

export function GetEngagementDetailsToolCall({
  toolCall,
}: {
  toolCall: GetEngagementDetailsToolCallSummary;
}) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('prince');
  const userTimezone = useUserTimezone();

  const { parsedResult, title } = useToolCallParser<GetEngagementDetailsResult>(
    toolCall,
    ({ toolCallState }) => {
      switch (toolCallState) {
        case ToolCallState.Running:
          return t('toolCalls.chorus_get_engagement_details.title.whileRunning');
        case ToolCallState.Error:
          return t('toolCalls.chorus_get_engagement_details.title.errors');
        case ToolCallState.Success:
          return t('toolCalls.chorus_get_engagement_details.title.success');
      }
    }
  );

  const getFormattedMeetingDate = useCallback(
    (date: string) =>
      tCommon('format.date', {
        date: new Date(date),
        formatParams: {
          date: {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            timezone: userTimezone,
          },
        },
      }),
    [tCommon, userTimezone]
  );

  const formattedSummary = useMemo(() => {
    if (!parsedResult?.results?.summary) return '';
    return formatSummaryContent(parsedResult.results.summary);
  }, [parsedResult]);

  const tabs = [
    {
      id: 'results',
      label: t('toolCalls.results'),
      showLoadingWhileRunning: true,
      content: parsedResult ? (
        !parsedResult.hasErrors ? (
          <MeetingCard
            subject={parsedResult.results.subject}
            formattedDate={getFormattedMeetingDate(parsedResult.results.date)}
            participants={(parsedResult.results.participants || []).map(participant => ({
              id: participant.person_id.toString(),
              name: participant.name,
            }))}
            accountName={parsedResult.results.account.name}
          >
            <div className="space-y-4">
              {/* Summary Section */}
              {parsedResult.results.summary && (
                <div className="font-normal text-sm leading-relaxed text-gray-700">
                  <ChatMarkDownFormatter content={formattedSummary} />
                </div>
              )}
            </div>
          </MeetingCard>
        ) : (
          <ToolCallError parsedResult={parsedResult} />
        )
      ) : null,
    },
    {
      id: 'parameters',
      label: t('toolCalls.parameters'),
      content: (
        <ToolCallDataList
          items={[
            {
              label: t('toolCalls.chorus_get_engagement_details.parameters.engagementId'),
              value: toolCall.args.engagementId,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <ToolCallContainer
      toolName={toolCall.name}
      title={title}
      isRunning={toolCall.isRunning}
      tabs={tabs}
    />
  );
}
